import { useKomutStore } from "@lib/store";
import { Wifi, WifiOff } from "lucide-react";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { cn } from "@utils/index";

const RESTORED_BANNER_MS = 2500;

export function OfflineBanner() {
  const isOnline = useKomutStore((state) => state.isOnline);
  const [showRestored, setShowRestored] = useState(false);
  const previousOnlineRef = useRef(isOnline);
  const hideRestoredTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isClient = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  useEffect(() => {
    const clearHideTimer = () => {
      if (hideRestoredTimerRef.current !== null) {
        window.clearTimeout(hideRestoredTimerRef.current);
        hideRestoredTimerRef.current = null;
      }
    };

    const wasOnline = previousOnlineRef.current;
    previousOnlineRef.current = isOnline;

    if (!isOnline) {
      clearHideTimer();
      setShowRestored(false);
      return;
    }

    if (!wasOnline && isOnline) {
      setShowRestored(true);
      clearHideTimer();
      hideRestoredTimerRef.current = window.setTimeout(() => {
        setShowRestored(false);
        hideRestoredTimerRef.current = null;
      }, RESTORED_BANNER_MS);
    }

    return clearHideTimer;
  }, [isOnline]);

  useEffect(() => () => {
    if (hideRestoredTimerRef.current !== null) {
      window.clearTimeout(hideRestoredTimerRef.current);
    }
  }, []);

  if (!isClient) return null;

  const isVisible = !isOnline || showRestored;
  if (!isVisible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={cn(
        "fixed left-0 right-0 top-0 z-[90] pt-[var(--safe-area-top)] transition-[transform,opacity] duration-300 ease-out",
        isVisible
          ? "translate-y-0 opacity-100"
          : "-translate-y-full opacity-0 pointer-events-none",
      )}
    >
      <div
        className={cn(
          "w-full px-4 py-[1px] transition-colors duration-300 sm:px-6 lg:px-8",
          showRestored
            ? "bg-green-500/90 text-white"
            : "bg-orange-500/90 text-white",
        )}
      >
        <div className="flex items-center justify-center gap-2 text-xs font-medium md:text-sm">
          {showRestored ? (
            <>
              <Wifi className="h-4 w-4" />
              <span>Connection restored</span>
            </>
          ) : (
            <>
              <WifiOff className="h-4 w-4" />
              <span>You&apos;re offline.</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
